// core/interceptors/error.interceptor.ts
import { Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { telemetry } from '../telemetry/telemetry';
import { ToastrService } from 'ngx-toastr';
import { isReadOnlyError, READ_ONLY_TOAST_MESSAGE } from '../utils/read-only-session';
// import { AuthService } from '../services/auth.service';

export interface AppError {
  status: number;
  message: string;
  correlationId: string | null;
  timestamp: string;
  url: string | null;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  // Last read-only toast time — a page firing several blocked writes at once
  // shows ONE toast, not a stack of them.
  private lastReadOnlyToastAt = 0;

  constructor(
    private router: Router, // private authService: AuthService
    private injector: Injector
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Read-only session (suspended store, token claim readOnly:true): the backend
        // refused a write. Expected, not a bug — toast once, no telemetry event, no
        // logout/refresh. Only a 403 whose body is {error:'read_only'} lands here, which
        // no service returns for a token without the claim.
        if (isReadOnlyError(error)) {
          const roError = { ...this.buildError(error, req), message: READ_ONLY_TOAST_MESSAGE };
          telemetry.breadcrumb({
            category: 'auth',
            level: 'info',
            message: `read_only_blocked ${req.method} ${roError.url}`,
            data: { route: this.router.url }
          });
          this.showReadOnlyToast();
          return throwError(() => roError);
        }
        const appError = this.buildError(error, req);
        this.reportError(error, appError, req);
        this.handleByStatus(error.status, appError);
        return throwError(() => appError);
      })
    );
  }

  private reportError(error: HttpErrorResponse, appError: AppError, req: HttpRequest<any>): void {
    // 401s are expected (session expiry) — drop a breadcrumb so the next captured
    // event still shows the user was bounced, but don't raise an event.
    if (error.status === 401) {
      telemetry.breadcrumb({
        category: 'auth',
        level: 'info',
        message: 'session_expired',
        data: { url: appError.url, route: this.router.url }
      });
      return;
    }

    // Client/network errors (status 0) — usually offline or CORS preflight.
    // Breadcrumb only; the user already gets a toast.
    if (error.status === 0) {
      telemetry.breadcrumb({
        category: 'http',
        level: 'warning',
        message: `network_error ${req.method} ${appError.url}`,
        data: { route: this.router.url }
      });
      return;
    }

    const tags: Record<string, string> = {
      'http.status': String(error.status),
      'http.method': req.method
    };
    if (appError.correlationId) tags['correlation_id'] = appError.correlationId;

    const httpContext = {
      url: appError.url,
      method: req.method,
      status: error.status,
      message: appError.message,
      correlationId: appError.correlationId,
      route: this.router.url
    };

    // 5xx → exception (server bug). 403/404/409 → warning message (config/auth bug,
    // actionable). Other 4xx (400/422 etc) → breadcrumb only (validation noise).
    // 5xx → exception (server bug). 4xx → warning or breadcrumb.
    if (error.status >= 500) {
      telemetry.captureException(error, { tags, contexts: { http: httpContext } });
    } else if ([400, 401, 403, 404, 409, 422].includes(error.status)) {
      telemetry.captureMessage(`${req.method} ${appError.url} -> ${error.status}: ${appError.message}`, {
        level: error.status === 401 ? 'info' : 'warning',
        tags,
        contexts: { http: httpContext }
      });
    } else {
      telemetry.breadcrumb({
        category: 'http',
        level: 'warning',
        message: `${req.method} ${appError.url} -> ${error.status}`,
        data: { correlationId: appError.correlationId, route: this.router.url }
      });
    }
  }

  private handleByStatus(status: number, error: AppError): void {
    switch (status) {
      case 0:
        this.showToast('No internet connection. Please check your network.', 'error');
        break;

      case 400:
        this.showToast(error.message || 'Invalid request. Please check your input.', 'error');
        break;

      case 401:
        this.router.navigate(['/auth/login'], {
          queryParams: { returnUrl: this.router.url }
        });
        this.showToast('Session expired. Please login again.', 'warning');
        break;

      case 403:
        this.showToast('You do not have permission to perform this action.', 'error');
        break;

      case 404:
        this.showToast('The requested resource was not found.', 'error');
        break;

      case 408:
        this.showToast('Request timed out. Please try again.', 'warning');
        break;

      case 409:
        this.showToast('Conflict: This resource already exists.', 'error');
        break;

      case 422:
        this.showToast('Validation failed. Please check your input.', 'error');
        break;

      case 429:
        this.showToast('Too many requests. Please slow down and try again.', 'warning');
        break;

      case 500:
        this.showToast(`Something went wrong. Reference ID: ${error.correlationId}`, 'error');
        break;

      case 502:
      case 503:
      case 504:
        this.showToast('Server is temporarily unavailable. Please try again shortly.', 'error');
        break;

      default:
        this.showToast(`Unexpected error (${error.status}). Reference: ${error.correlationId}`, 'error');
    }
  }

  private buildError(error: HttpErrorResponse, req: HttpRequest<any>): AppError {
    return {
      status: error.status,
      message: error.error?.error || error.error?.message || error.message || 'An unexpected error occurred',
      correlationId: error.headers?.get('X-Correlation-ID') || req.headers.get('X-Correlation-ID'),
      timestamp: new Date().toISOString(),
      url: error.url
    };
  }

  // ToastrService is resolved lazily: injecting it (or CommonUtilityService,
  // which pulls TranslateService -> HttpClient) into an interceptor's
  // constructor creates a DI cycle.
  private showReadOnlyToast(): void {
    const now = Date.now();
    if (now - this.lastReadOnlyToastAt < 3000) return;
    this.lastReadOnlyToastAt = now;
    try {
      this.injector.get(ToastrService).show(READ_ONLY_TOAST_MESSAGE, '', {
        closeButton: true,
        newestOnTop: true,
        positionClass: 'toast-top-right',
        timeOut: 4000,
        extendedTimeOut: 1000,
        toastClass: 'ngx-toastr toast-warning'
      });
    } catch {
      // Toastr unavailable (e.g. during bootstrap) — the banner still explains it.
    }
  }

  private showToast(message: string, type: 'error' | 'warning' | 'info'): void {
    if (typeof window !== 'undefined' && !(window as any).__environment?.production) {
      console.error(`[${type.toUpperCase()}] ${message}`);
    }
  }
}
